<template>
  <div class="editor-layout">
    <!-- Editor header bar -->
    <div class="editor-topbar">
      <div class="editor-topbar-left">
        <button @click="$emit('cancel')" class="btn-back">
          <i class="fas fa-arrow-left"></i>
        </button>
        <div>
          <h2 class="editor-title">
            {{ structure ? (structure.name_en || structure.name_fr || 'Edit Structure') : 'Register New Structure' }}
          </h2>
          <span v-if="structure" :class="['status-chip', `s-${structure.status}`]">{{ structure.status }}</span>
        </div>
      </div>
      <!-- Progress -->
      <div class="editor-progress">
        <div class="ep-bar-wrap">
          <div class="ep-bar" :style="{ width: localPercent + '%' }"></div>
        </div>
        <span class="ep-label">{{ localPercent }}% complete</span>
      </div>
    </div>

    <!-- Approved notice -->
    <div v-if="structure?.status === 'approved'" class="approved-notice">
      <i class="fas fa-check-circle mr-2"></i>
      This structure is <strong>approved</strong> and visible on the public portal.
      You can still update its information — changes will be reviewed before going live.
    </div>

    <div class="editor-body">
      <!-- Section tabs (left sidebar) -->
      <nav class="section-nav">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['section-btn', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          <i :class="tab.icon + ' section-icon'"></i>
          <span>{{ tab.label }}</span>
        </button>
      </nav>

      <!-- Form content -->
      <div class="section-content">

        <!-- ── 0. SECTOR (shown on info tab prominently) ─────────────── -->
        <section v-show="activeTab === 'info'">
          <h3 class="section-heading">Structure Information</h3>
          <div class="form-grid">
            <div class="form-group">
              <label>Name (English)</label>
              <input v-model="form.name_en" type="text" placeholder="Ministry of..." />
            </div>
            <div class="form-group">
              <label>Name (French)</label>
              <input v-model="form.name_fr" type="text" placeholder="Ministère de..." />
            </div>
            <div class="form-group">
              <label>Abbreviation (EN)</label>
              <input v-model="form.abbreviation_en" type="text" placeholder="MOD" />
            </div>
            <div class="form-group">
              <label>Abbreviation (FR)</label>
              <input v-model="form.abbreviation_fr" type="text" placeholder="MINDEF" />
            </div>
            <div class="form-group col-span-2">
              <label>Description (English)</label>
              <textarea v-model="form.description_en" rows="3" placeholder="Brief description of your structure..."></textarea>
            </div>
            <div class="form-group col-span-2">
              <label>Description (French)</label>
              <textarea v-model="form.description_fr" rows="3" placeholder="Description brève de votre structure..."></textarea>
            </div>

            <!-- Photo upload with size specs -->
            <div class="form-group col-span-2">
              <label>Structure Logo / Photo</label>
              <div class="photo-upload-wrap">
                <img v-if="structurePhotoPreview" :src="structurePhotoPreview" class="photo-preview" alt="Preview" />
                <div class="photo-upload-right">
                  <label for="structure-photo" class="photo-upload-label">
                    <i class="fas fa-camera mr-2"></i>
                    {{ structurePhotoPreview ? 'Change Photo' : 'Upload Photo' }}
                    <input id="structure-photo" type="file" accept="image/jpeg,image/png,image/webp"
                           @change="onStructurePhotoChange" hidden />
                  </label>
                  <ul class="photo-specs">
                    <li><i class="fas fa-check-circle text-green-500 mr-1"></i> Recommended: <strong>800 × 600 px</strong> or larger</li>
                    <li><i class="fas fa-check-circle text-green-500 mr-1"></i> Max file size: <strong>2 MB</strong></li>
                    <li><i class="fas fa-check-circle text-green-500 mr-1"></i> Formats: <strong>JPG, PNG, WebP</strong></li>
                    <li><i class="fas fa-info-circle text-blue-400 mr-1"></i> Square or landscape ratio preferred</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ── 1. CONTACT ─────────────────────────────────────────────── -->
        <section v-show="activeTab === 'contact'">
          <h3 class="section-heading">Contact Information</h3>
          <div class="form-grid">
            <div class="form-group">
              <label>Phone <span class="required">*</span></label>
              <input v-model="form.phone" type="tel" placeholder="+237 6XX XXX XXX" />
            </div>
            <div class="form-group">
              <label>Email <span class="required">*</span></label>
              <input v-model="form.email" type="email" placeholder="contact@structure.cm" />
            </div>
            <div class="form-group">
              <label>Website</label>
              <input v-model="form.website_url" type="url" placeholder="https://www.example.cm" />
            </div>
            <div class="form-group">
              <label>Location <span class="field-hint">(Neighborhood, City, Region)</span></label>
              <input v-model="form.location" type="text"
                     placeholder="e.g. Bastos, Yaoundé, Centre" />
            </div>
            <div class="form-group col-span-2">
              <label>Google Maps Link or GPS Coordinates</label>
              <input v-model="form.google_map_directions" type="text"
                     placeholder="https://maps.google.com/... or 3.848, 11.502" />
            </div>
          </div>

          <h3 class="section-heading mt-6">Social Media</h3>
          <div class="form-grid">
            <div v-for="field in socialFields" :key="field.key" class="form-group">
              <label><i :class="field.icon + ' mr-1'"></i> {{ field.label }}</label>
              <input v-model="form[field.key]" type="url" placeholder="https://" />
            </div>
          </div>
        </section>

        <!-- ── 2. SERVICES ────────────────────────────────────────────── -->
        <section v-show="activeTab === 'services'">
          <h3 class="section-heading">Services & Missions</h3>
          <div v-for="(svc, i) in services" :key="i" class="nested-item" v-show="!svc._deleted">
            <div class="form-grid">
              <div class="form-group">
                <label>Title (EN)</label>
                <input v-model="svc.title_en" type="text" />
              </div>
              <div class="form-group">
                <label>Title (FR)</label>
                <input v-model="svc.title_fr" type="text" />
              </div>
              <div class="form-group col-span-2">
                <label>Description (EN)</label>
                <textarea v-model="svc.description_en" rows="2"></textarea>
              </div>
              <div class="form-group col-span-2">
                <label>Description (FR)</label>
                <textarea v-model="svc.description_fr" rows="2"></textarea>
              </div>
            </div>
            <button @click="removeService(i)" class="btn-remove">
              <i class="fas fa-trash mr-1"></i> Remove
            </button>
          </div>
          <button @click="addService" class="btn-add">
            <i class="fas fa-plus mr-1"></i> Add Service
          </button>
        </section>

        <!-- ── 3. PERSONNEL ───────────────────────────────────────────── -->
        <section v-show="activeTab === 'personnel'">
          <h3 class="section-heading">Key Personnel</h3>
          <div v-for="(p, i) in personnel" :key="i" class="nested-item" v-show="!p._deleted">
            <div class="form-grid">
              <div class="form-group col-span-2">
                <label>Full Name</label>
                <input v-model="p.name" type="text" />
              </div>
              <div class="form-group">
                <label>Title (EN)</label>
                <input v-model="p.title_en" type="text" />
              </div>
              <div class="form-group">
                <label>Title (FR)</label>
                <input v-model="p.title_fr" type="text" />
              </div>
              <div class="form-group">
                <label>Phone</label>
                <input v-model="p.phone" type="tel" />
              </div>
              <div class="form-group">
                <label>Email</label>
                <input v-model="p.email" type="email" />
              </div>
              <div class="form-group col-span-2">
                <label>
                  Photo
                  <span class="field-hint">(JPG/PNG, max 1MB, 400×400px recommended)</span>
                </label>
                <div class="photo-upload-wrap">
                  <img v-if="p.photo_url || p._previewUrl" :src="p._previewUrl || p.photo_url"
                       class="photo-preview-sm" alt="Preview" />
                  <label :for="`person-photo-${i}`" class="photo-upload-label">
                    <i class="fas fa-camera mr-1"></i>
                    {{ (p.photo_url || p._previewUrl) ? 'Change' : 'Upload Photo' }}
                    <input :id="`person-photo-${i}`" type="file" accept="image/*"
                           @change="onPersonnelPhotoChange(i, $event)" hidden />
                  </label>
                </div>
              </div>
              <div class="form-group col-span-2">
                <label class="checkbox-label">
                  <input v-model="p.is_head" type="checkbox" />
                  <span>Head of Structure / Director General</span>
                </label>
              </div>
            </div>
            <button @click="removePersonnel(i)" class="btn-remove">
              <i class="fas fa-trash mr-1"></i> Remove
            </button>
          </div>
          <button @click="addPersonnel" class="btn-add">
            <i class="fas fa-plus mr-1"></i> Add Personnel
          </button>
        </section>

        <!-- ── 4. DELEGATIONS ────────────────────────────────────────── -->
        <section v-show="activeTab === 'delegations'">
          <h3 class="section-heading">Regional Delegations</h3>
          <div v-for="(d, i) in delegations" :key="i" class="nested-item" v-show="!d._deleted">
            <div class="form-grid">
              <div class="form-group">
                <label>Region</label>
                <select v-model="d.region">
                  <option v-for="r in regions" :key="r.value" :value="r.value">{{ r.label }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Contact Name</label>
                <input v-model="d.contact_name" type="text" />
              </div>
              <div class="form-group">
                <label>Name (EN)</label>
                <input v-model="d.name_en" type="text" />
              </div>
              <div class="form-group">
                <label>Name (FR)</label>
                <input v-model="d.name_fr" type="text" />
              </div>
              <div class="form-group">
                <label>Phone</label>
                <input v-model="d.phone" type="tel" />
              </div>
              <div class="form-group">
                <label>Email</label>
                <input v-model="d.email" type="email" />
              </div>
              <div class="form-group">
                <label>Location (EN) <span class="field-hint">(Neighborhood, City)</span></label>
                <input v-model="d.location_en" type="text" placeholder="e.g. Bonanjo, Douala" />
              </div>
              <div class="form-group">
                <label>Location (FR) <span class="field-hint">(Quartier, Ville)</span></label>
                <input v-model="d.location_fr" type="text" placeholder="ex. Bonanjo, Douala" />
              </div>
            </div>
            <button @click="removeDelegation(i)" class="btn-remove">
              <i class="fas fa-trash mr-1"></i> Remove
            </button>
          </div>
          <button @click="addDelegation" class="btn-add">
            <i class="fas fa-plus mr-1"></i> Add Delegation
          </button>
        </section>

        <!-- ── 5. CONFORMITY ──────────────────────────────────────────── -->
        <section v-show="activeTab === 'conformity'">
          <h3 class="section-heading">Legal Conformity</h3>
          <p class="section-hint">
            Select the legal requirements your structure currently meets.
            This information is used to compute your conformity score on the public portal.
          </p>
          <div v-if="conformityLoading" class="text-center py-6">
            <i class="fas fa-spinner fa-spin text-gray-400 fa-2x"></i>
          </div>
          <div v-else-if="conformityCategories.length" class="conformity-categories">
            <div v-for="cat in conformityCategories" :key="cat.id" class="conformity-cat">
              <label class="cat-checkbox">
                <input type="checkbox" :value="cat.id" v-model="form.selected_category_ids" />
                <span class="cat-name">{{ cat.name }}</span>
              </label>
              <p class="cat-desc">{{ cat.description }}</p>
              <div v-if="form.selected_category_ids.includes(cat.id)" class="requirements-list">
                <p class="req-hint">Tick each requirement your structure currently fulfills:</p>
                <label v-for="req in cat.requirements" :key="req.id" class="req-item">
                  <input type="checkbox" :value="req.id" v-model="form.ticked_requirement_ids" />
                  <span>
                    <strong>{{ req.name }}</strong>
                    <span v-if="req.is_physical" class="physical-tag ml-1">
                      <i class="fas fa-building"></i> Physical verification
                    </span>
                    <br /><small>{{ req.description }}</small>
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div v-else class="info-note">
            <i class="fas fa-info-circle mr-2"></i>
            Conformity categories will be available once your sector is selected and saved.
          </div>
        </section>

        <!-- ── 6. DOCUMENT ────────────────────────────────────────────── -->
        <section v-show="activeTab === 'document'">
          <h3 class="section-heading">Documents</h3>

          <div v-if="!structureId" class="info-note">
            <i class="fas fa-save mr-1"></i>
            Save your structure first to generate the official form document.
          </div>

          <template v-else>
            <!-- Official form download -->
            <div class="doc-block">
              <h4 class="doc-block-title">
                <i class="fas fa-file-alt mr-2 text-blue-600"></i> Official Registration Form
              </h4>
              <p class="section-hint">
                Download the pre-filled form, print it, have it signed and officially stamped by
                the responsible authority, then upload the scanned copy below.
              </p>
              <div class="doc-actions">
                <button @click="downloadDoc('pdf')" class="btn-doc">
                  <i class="fas fa-file-pdf mr-1 text-red-500"></i> Download PDF
                </button>
                <button @click="downloadDoc('docx')" class="btn-doc">
                  <i class="fas fa-file-word mr-1 text-blue-500"></i> Download Word
                </button>
              </div>
            </div>

            <!-- Signed document upload -->
            <div class="doc-block">
              <h4 class="doc-block-title">
                <i class="fas fa-upload mr-2 text-green-600"></i> Upload Signed Document
              </h4>
              <p class="section-hint">
                Upload the signed and stamped version of the registration form.
                Accepted formats: PDF, JPG, PNG (max 5MB).
              </p>
              <div v-if="structure?.signed_document_url" class="uploaded-file-row">
                <i class="fas fa-check-circle text-green-500 mr-2"></i>
                <span>Signed document uploaded</span>
                <a :href="structure.signed_document_url" target="_blank" class="view-link ml-2">
                  <i class="fas fa-eye mr-1"></i> View
                </a>
              </div>
              <div class="upload-zone">
                <label for="signed-upload" class="upload-label">
                  <i class="fas fa-cloud-upload-alt upload-icon"></i>
                  <span>{{ structure?.signed_document_url ? 'Replace Signed Document' : 'Upload Signed Document' }}</span>
                  <small>PDF, JPG or PNG — max 5 MB</small>
                  <input id="signed-upload" type="file" accept=".pdf,.png,.jpg,.jpeg"
                         @change="handleSignedUpload" hidden />
                </label>
              </div>
              <p v-if="uploadMsg" class="upload-msg"><i class="fas fa-check-circle mr-1"></i> {{ uploadMsg }}</p>
            </div>

            <!-- Conformity supporting documents -->
            <div class="doc-block">
              <h4 class="doc-block-title">
                <i class="fas fa-shield-alt mr-2 text-purple-600"></i> Conformity Supporting Documents
              </h4>
              <p class="section-hint">
                You may upload any document that testifies to your structure's conformity with
                legal requirements in your sector or category. Examples include:
                operating licenses, tax clearance certificates, registration certificates,
                sector-specific authorizations, audit reports, or any official document issued
                by a regulatory body. These documents support your conformity claims and will
                be reviewed by ANTIC during the approval process.
              </p>
              <div class="upload-zone upload-zone-secondary">
                <label for="conformity-doc-upload" class="upload-label">
                  <i class="fas fa-folder-open upload-icon" style="color:#7c3aed"></i>
                  <span>Upload Conformity Document</span>
                  <small>PDF, JPG or PNG — max 10 MB</small>
                  <input id="conformity-doc-upload" type="file" accept=".pdf,.png,.jpg,.jpeg"
                         @change="handleConformityDocUpload" hidden />
                </label>
              </div>
              <div v-if="conformityDocs.length" class="uploaded-files-list">
                <div v-for="(doc, i) in conformityDocs" :key="i" class="uploaded-file-row">
                  <i class="fas fa-file mr-2 text-purple-500"></i>
                  <span class="doc-name">{{ doc.name }}</span>
                  <span class="doc-size text-gray-400 text-xs ml-2">({{ formatSize(doc.size) }})</span>
                </div>
              </div>
              <p v-if="conformityUploadMsg" class="upload-msg"><i class="fas fa-check-circle mr-1"></i> {{ conformityUploadMsg }}</p>
            </div>
          </template>
        </section>
      </div>
    </div>

    <!-- Footer action bar -->
    <div class="editor-footer">
      <div v-if="error" class="footer-error">
        <i class="fas fa-exclamation-triangle mr-1"></i> {{ error }}
      </div>
      <div v-if="incompleteWarning" class="footer-warning">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        Some required fields are missing. Please complete all required sections before submitting.
      </div>
      <div class="footer-btns">
        <button @click="$emit('cancel')" class="btn-cancel">Cancel</button>
        <button @click="saveStructure" class="btn-save" :disabled="saving">
          <i v-if="saving" class="fas fa-spinner fa-spin mr-1"></i>
          {{ saving ? 'Saving...' : 'Save as Draft' }}
        </button>
        <button
          v-if="!['submitted', 'approved'].includes(structure?.status)"
          @click="submitForReview"
          class="btn-submit"
          :disabled="saving || submitting"
        >
          <i v-if="submitting" class="fas fa-spinner fa-spin mr-1"></i>
          {{ submitting ? 'Submitting...' : 'Submit for Review' }}
        </button>
        <span v-else-if="structure?.status === 'submitted'" class="status-info">
          <i class="fas fa-clock mr-1"></i> Under review — edits will be queued
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef, no-unused-vars */
import { ref, reactive, computed, onMounted } from 'vue';
import { useRegistrationStore } from '@/stores/registration';

const props = defineProps({ structure: { type: Object, default: null } });
const emit  = defineEmits(['cancel', 'saved']);

const store    = useRegistrationStore();
const MAIN_API = process.env.VUE_APP_API_BASE_URL || '/api';

const activeTab = ref('info');
const saving    = ref(false);
const submitting = ref(false);
const error     = ref('');
const uploadMsg = ref('');
const conformityUploadMsg = ref('');
const incompleteWarning = ref(false);
const structureId = ref(props.structure?.id || null);
const conformityCategories = ref([]);
const conformityLoading = ref(false);
const sectors = ref([]);
const sectorsLoading = ref(false);
const conformityDocs = ref([]);

const form = reactive({
  name_en: '', name_fr: '', abbreviation_en: '', abbreviation_fr: '',
  description_en: '', description_fr: '',
  state_order_id: null,
  phone: '', email: '', website_url: '', location: '', google_map_directions: '',
  facebook_url: '', twitter_url: '', instagram_url: '', linkedin_url: '',
  tiktok_url: '', youtube_url: '',
  selected_category_ids: [],
  ticked_requirement_ids: [],
});

const services    = ref([]);
const personnel   = ref([]);
const delegations = ref([]);

const structurePhotoFile = ref(null);
const structurePhotoPreview = ref(props.structure?.photo_url || null);

const tabs = [
  { id: 'info',        icon: 'fas fa-info-circle',     label: 'Info & Sector' },
  { id: 'contact',     icon: 'fas fa-address-card',    label: 'Contact' },
  { id: 'services',    icon: 'fas fa-concierge-bell',  label: 'Services' },
  { id: 'personnel',   icon: 'fas fa-users',           label: 'Personnel' },
  { id: 'delegations', icon: 'fas fa-map-marker-alt',  label: 'Branches' },
  { id: 'conformity',  icon: 'fas fa-shield-alt',      label: 'Conformity' },
  { id: 'document',    icon: 'fas fa-file-signature',  label: 'Documents' },
];

const socialFields = [
  { key: 'facebook_url',  icon: 'fab fa-facebook',  label: 'Facebook' },
  { key: 'twitter_url',   icon: 'fab fa-twitter',   label: 'Twitter / X' },
  { key: 'instagram_url', icon: 'fab fa-instagram', label: 'Instagram' },
  { key: 'linkedin_url',  icon: 'fab fa-linkedin',  label: 'LinkedIn' },
  { key: 'tiktok_url',    icon: 'fab fa-tiktok',    label: 'TikTok' },
  { key: 'youtube_url',   icon: 'fab fa-youtube',   label: 'YouTube' },
];

const regions = [
  { value: 'adamawa',    label: 'Adamawa' },    { value: 'centre',     label: 'Centre' },
  { value: 'east',       label: 'East' },       { value: 'far_north',  label: 'Far North' },
  { value: 'littoral',   label: 'Littoral' },   { value: 'north',      label: 'North' },
  { value: 'north_west', label: 'North West' }, { value: 'west',       label: 'West' },
  { value: 'south',      label: 'South' },      { value: 'south_west', label: 'South West' },
];

const localPercent = computed(() => {
  const checks = [
    form.name_en || form.name_fr,
    form.description_en || form.description_fr,
    form.state_order_id,
    form.email, form.phone,
    form.website_url, form.location,
    form.facebook_url || form.twitter_url || form.instagram_url || form.linkedin_url,
    structurePhotoPreview.value || props.structure?.photo_url,
    services.value.filter(s => !s._deleted).length > 0,
    personnel.value.filter(p => !p._deleted).length > 0,
    props.structure?.signed_document_url,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
});

onMounted(async () => {
  // Load sectors
  sectorsLoading.value = true;
  try {
    const r = await fetch(`${MAIN_API}/state-orders/`);
    if (r.ok) sectors.value = await r.json();
  } catch (e) { console.warn('Could not load sectors', e); } finally { sectorsLoading.value = false; }

  // Load conformity categories
  conformityLoading.value = true;
  try {
    const r = await fetch(`${MAIN_API}/legal-conformity-categories/`);
    if (r.ok) conformityCategories.value = await r.json();
  } catch (e) { console.warn('Could not load conformity categories', e); } finally { conformityLoading.value = false; }

  // Prefill from existing structure
  if (props.structure) {
    Object.keys(form).forEach(k => {
      if (props.structure[k] !== undefined && props.structure[k] !== null)
        form[k] = props.structure[k];
    });
    services.value    = [...(props.structure.services || [])];
    personnel.value   = [...(props.structure.key_personnel || [])];
    delegations.value = [...(props.structure.regional_delegations || [])];
  }
});

// ── Photo helpers ────────────────────────────────────────────────────────────
function onStructurePhotoChange(event) {
  const file = event.target.files[0];
  if (!file) return;
  structurePhotoFile.value = file;
  structurePhotoPreview.value = URL.createObjectURL(file);
}
function onPersonnelPhotoChange(index, event) {
  const file = event.target.files[0];
  if (!file) return;
  personnel.value[index]._photoFile = file;
  personnel.value[index]._previewUrl = URL.createObjectURL(file);
}

// ── List mutations ───────────────────────────────────────────────────────────
function addService() { services.value.push({ title_en:'', title_fr:'', description_en:'', description_fr:'' }); }
function removeService(i) {
  if (services.value[i].id) services.value[i]._deleted = true;
  else services.value.splice(i, 1);
}
function addPersonnel() { personnel.value.push({ name:'', title_en:'', title_fr:'', phone:'', email:'', is_head:false }); }
function removePersonnel(i) {
  if (personnel.value[i].id) personnel.value[i]._deleted = true;
  else personnel.value.splice(i, 1);
}
function addDelegation() { delegations.value.push({ region:'centre', name_en:'', name_fr:'', contact_name:'', phone:'', email:'', location_en:'', location_fr:'' }); }
function removeDelegation(i) {
  if (delegations.value[i].id) delegations.value[i]._deleted = true;
  else delegations.value.splice(i, 1);
}

// ── Save ─────────────────────────────────────────────────────────────────────
async function saveStructure() {
  saving.value = true; error.value = '';
  try {
    let payload;
    if (structurePhotoFile.value) {
      payload = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (Array.isArray(v)) payload.append(k, JSON.stringify(v));
        else if (v !== null && v !== undefined) payload.append(k, v);
      });
      payload.append('photo', structurePhotoFile.value);
    } else {
      payload = { ...form };
    }
    const saved = await store.saveStructure(structureId.value, payload);
    if (!saved?.id) throw new Error('Save did not return an ID.');
    structureId.value = saved.id;

    for (const svc of services.value) {
      if (svc._deleted && svc.id) await store.deleteService(saved.id, svc.id);
      else if (svc.id) await store.updateService(saved.id, svc.id, svc);
      else await store.addService(saved.id, svc);
    }
    for (const p of personnel.value) {
      if (p._deleted && p.id) { await store.deletePersonnel(saved.id, p.id); continue; }
      let pPayload;
      if (p._photoFile) {
        pPayload = new FormData();
        Object.entries(p).forEach(([k, v]) => {
          if (!['_photoFile','_deleted','id','photo','photo_url'].includes(k) && v !== null && v !== undefined)
            pPayload.append(k, v);
        });
        pPayload.append('photo', p._photoFile);
      } else {
        const { _photoFile, _deleted, photo_url, ...clean } = p; // eslint-disable-line
        pPayload = clean;
      }
      if (p.id) await store.updatePersonnel(saved.id, p.id, pPayload);
      else await store.addPersonnel(saved.id, pPayload);
    }
    for (const d of delegations.value) {
      if (d._deleted && d.id) await store.deleteDelegation(saved.id, d.id);
      else if (d.id) await store.updateDelegation(saved.id, d.id, d);
      else await store.addDelegation(saved.id, d);
    }
    emit('saved');
  } catch (e) {
    error.value = e.message;
  } finally {
    saving.value = false;
  }
}

async function submitForReview() {
  if (!structureId.value) {
    await saveStructure();
    if (!structureId.value) return;
  }
  incompleteWarning.value = false;
  submitting.value = true; error.value = '';
  try {
    await store.submitStructure(structureId.value);
    emit('saved');
  } catch (e) {
    if (e.message?.toLowerCase().includes('incomplete')) incompleteWarning.value = true;
    else error.value = e.message;
  } finally {
    submitting.value = false;
  }
}

async function downloadDoc(format) {
  const url = store.getDownloadUrl(structureId.value, format);
  const res = await fetch(url, { headers: store.authHeaders });
  if (!res.ok) { error.value = 'Download failed'; return; }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `structure_form.${format}`;
  a.click();
}

async function handleSignedUpload(event) {
  const file = event.target.files[0];
  if (!file) return;
  try {
    await store.uploadSigned(structureId.value, file);
    uploadMsg.value = 'Signed document uploaded successfully!';
  } catch (e) { error.value = e.message; }
}

async function handleConformityDocUpload(event) {
  const file = event.target.files[0];
  if (!file) return;
  conformityDocs.value.push({ name: file.name, size: file.size });
  conformityUploadMsg.value = `"${file.name}" added. It will be submitted with your application.`;
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
</script>

<style scoped>
.editor-layout { display:flex; flex-direction:column; height:100%; background:#f8fafc; }

/* Top bar */
.editor-topbar {
  background:#fff; border-bottom:1px solid #e5e7eb; padding:14px 24px;
  display:flex; align-items:center; justify-content:space-between; gap:16px; flex-shrink:0;
}
.editor-topbar-left { display:flex; align-items:center; gap:14px; }
.btn-back { background:#f3f4f6; border:1px solid #e5e7eb; color:#374151; border-radius:8px;
  width:36px; height:36px; display:flex; align-items:center; justify-content:center;
  cursor:pointer; font-size:.9rem; transition:background .2s; }
.btn-back:hover { background:#e5e7eb; }
.editor-title { font-size:1.05rem; font-weight:700; color:#1a3a5c; margin:0; }
.status-chip { border-radius:999px; padding:2px 10px; font-size:.7rem; font-weight:700;
  text-transform:uppercase; }
.s-draft { background:#f3f4f6; color:#6b7280; }
.s-submitted { background:#eff6ff; color:#2563eb; }
.s-approved { background:#f0fdf4; color:#16a34a; }
.s-rejected { background:#fef2f2; color:#dc2626; }
.s-revision_needed { background:#fffbeb; color:#d97706; }

.editor-progress { display:flex; align-items:center; gap:10px; }
.ep-bar-wrap { width:120px; background:#e5e7eb; border-radius:999px; height:6px; overflow:hidden; }
.ep-bar { background:linear-gradient(90deg,#1a3a5c,#3b82f6); height:100%; transition:width .4s; }
.ep-label { font-size:.78rem; color:#6b7280; white-space:nowrap; }

/* Approved notice */
.approved-notice { background:#f0fdf4; border-bottom:1px solid #bbf7d0;
  padding:10px 24px; font-size:.875rem; color:#166534; }

/* Body */
.editor-body { display:flex; flex:1; overflow:hidden; }

/* Left section nav */
.section-nav {
  width:180px; flex-shrink:0; background:#fff; border-right:1px solid #e5e7eb;
  padding:12px 0; overflow-y:auto;
}
.section-btn {
  display:flex; align-items:center; gap:9px; width:100%; padding:11px 18px;
  background:none; border:none; cursor:pointer; font-size:.84rem; color:#6b7280;
  text-align:left; transition:all .15s; border-left:3px solid transparent;
}
.section-btn:hover { background:#f9fafb; color:#1a3a5c; }
.section-btn.active { background:#eff6ff; color:#1a3a5c; font-weight:700;
  border-left-color:#1a3a5c; }
.section-icon { width:16px; text-align:center; font-size:.85rem; }

/* Section content */
.section-content { flex:1; overflow-y:auto; padding:28px 32px; }

/* Sector card */
.sector-select-card {
  background:#fff; border:1.5px solid #bfdbfe; border-radius:14px;
  padding:20px; margin-bottom:24px;
}
.sector-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(120px, 1fr)); gap:10px; margin-top:12px; }
.sector-option {
  display:flex; flex-direction:column; align-items:center; gap:6px;
  padding:10px 8px; border:2px solid #e5e7eb; border-radius:10px; cursor:pointer;
  background:#f9fafb; transition:all .2s; text-align:center;
}
.sector-option:hover { border-color:#93c5fd; background:#eff6ff; }
.sector-option.selected { border-color:#1a3a5c; background:#eff6ff; box-shadow:0 0 0 2px #bfdbfe; }
.sector-img { width:48px; height:48px; object-fit:cover; border-radius:6px; }
.sector-icon-fallback { font-size:2rem; color:#9ca3af; }
.sector-name { font-size:.75rem; font-weight:600; color:#374151; line-height:1.2; }

/* Form */
.section-heading { font-size:.95rem; font-weight:700; color:#1a3a5c;
  margin:0 0 14px; padding-bottom:8px; border-bottom:2px solid #e5e7eb; }
.section-hint { color:#6b7280; font-size:.875rem; margin-bottom:14px; line-height:1.5; }
.form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.col-span-2 { grid-column:span 2; }
.form-group { display:flex; flex-direction:column; gap:5px; }
.form-group label { font-size:.8rem; font-weight:600; color:#374151; }
.field-hint { font-weight:400; color:#9ca3af; font-size:.76rem; }
.form-group input, .form-group select, .form-group textarea {
  border:1.5px solid #d1d5db; border-radius:8px; padding:9px 12px;
  font-size:.875rem; width:100%; box-sizing:border-box; transition:border-color .2s; }
.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
  outline:none; border-color:#1a3a5c; }
.required { color:#ef4444; }

/* Photo */
.photo-upload-wrap { display:flex; align-items:flex-start; gap:16px; }
.photo-preview { width:80px; height:80px; object-fit:cover; border-radius:10px; border:1.5px solid #d1d5db; }
.photo-preview-sm { width:52px; height:52px; object-fit:cover; border-radius:8px; border:1.5px solid #d1d5db; }
.photo-upload-right { display:flex; flex-direction:column; gap:8px; }
.photo-upload-label { background:#eff6ff; color:#1d4ed8; border:1.5px dashed #93c5fd;
  border-radius:8px; padding:8px 16px; font-size:.8rem; cursor:pointer;
  display:flex; align-items:center; width:fit-content; }
.photo-specs { margin:0; padding:0 0 0 14px; font-size:.78rem; color:#6b7280; line-height:1.8; }

/* Nested items */
.nested-item { background:#f9fafb; border:1px solid #e5e7eb; border-radius:10px;
  padding:16px; margin-bottom:12px; }
.btn-remove { background:#fef2f2; color:#dc2626; border:1px solid #fca5a5; border-radius:7px;
  padding:5px 12px; font-size:.8rem; cursor:pointer; margin-top:10px; }
.btn-add { background:#dbeafe; color:#1d4ed8; border:1px solid #93c5fd; border-radius:8px;
  padding:8px 16px; font-size:.875rem; cursor:pointer; display:flex; align-items:center; gap:6px; }
.checkbox-label { display:flex; align-items:center; gap:8px; cursor:pointer; font-size:.875rem; }

/* Conformity */
.conformity-categories { display:flex; flex-direction:column; gap:14px; }
.conformity-cat { background:#f0f7ff; border:1px solid #bfdbfe; border-radius:10px; padding:14px; }
.cat-checkbox { display:flex; align-items:center; gap:8px; cursor:pointer; margin-bottom:6px; }
.cat-name { font-weight:700; color:#1e40af; }
.cat-desc { font-size:.8rem; color:#6b7280; margin:0 0 10px 24px; }
.requirements-list { margin-left:24px; display:flex; flex-direction:column; gap:8px; }
.req-hint { font-size:.8rem; color:#6b7280; margin-bottom:6px; }
.req-item { display:flex; align-items:flex-start; gap:8px; cursor:pointer; font-size:.875rem; }
.physical-tag { background:#dbeafe; color:#1d4ed8; border-radius:999px; padding:1px 8px;
  font-size:.7rem; font-weight:600; }

/* Documents */
.doc-block { background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:20px; margin-bottom:16px; }
.doc-block-title { font-size:.9rem; font-weight:700; color:#1a3a5c; margin:0 0 8px; }
.doc-actions { display:flex; gap:10px; margin-top:10px; flex-wrap:wrap; }
.btn-doc { background:#f3f4f6; border:1px solid #d1d5db; border-radius:8px; padding:9px 16px;
  font-size:.875rem; cursor:pointer; display:flex; align-items:center; gap:6px; }
.btn-doc:hover { background:#e5e7eb; }
.upload-zone { border:2px dashed #d1d5db; border-radius:12px; padding:28px; text-align:center;
  cursor:pointer; transition:border-color .2s; margin-top:10px; }
.upload-zone:hover { border-color:#1a3a5c; }
.upload-zone-secondary { border-color:#ddd6fe; }
.upload-zone-secondary:hover { border-color:#7c3aed; }
.upload-label { cursor:pointer; display:flex; flex-direction:column; align-items:center; gap:8px; }
.upload-icon { font-size:2rem; color:#9ca3af; }
.upload-label span { font-weight:600; color:#374151; font-size:.9rem; }
.upload-label small { color:#9ca3af; font-size:.8rem; }
.upload-msg { color:#059669; font-size:.875rem; margin-top:8px; }
.uploaded-file-row { display:flex; align-items:center; background:#f9fafb; border:1px solid #e5e7eb;
  border-radius:8px; padding:8px 14px; font-size:.875rem; margin-top:8px; }
.view-link { color:#2563eb; font-size:.8rem; text-decoration:none; }
.view-link:hover { text-decoration:underline; }
.uploaded-files-list { margin-top:10px; display:flex; flex-direction:column; gap:6px; }
.doc-name { flex:1; }
.info-note { background:#eff6ff; border:1px solid #bfdbfe; border-radius:8px; padding:14px;
  color:#1e40af; font-size:.875rem; }
.mt-6 { margin-top:1.5rem; }

/* Footer */
.editor-footer { border-top:1px solid #e5e7eb; padding:14px 24px; background:#fff;
  display:flex; flex-direction:column; gap:8px; flex-shrink:0; }
.footer-error { background:#fef2f2; border:1px solid #fca5a5; color:#b91c1c;
  border-radius:6px; padding:8px 12px; font-size:.8rem; }
.footer-warning { background:#fffbeb; border:1px solid #fde68a; color:#92400e;
  border-radius:6px; padding:8px 12px; font-size:.8rem; }
.footer-btns { display:flex; gap:10px; justify-content:flex-end; align-items:center; flex-wrap:wrap; }
.btn-cancel { background:#f3f4f6; color:#374151; border:1px solid #e5e7eb; border-radius:9px;
  padding:10px 20px; font-size:.9rem; cursor:pointer; flex:1; min-width:100px; text-align:center; }
.btn-save { background:#e5e7eb; color:#374151; border:none; border-radius:9px;
  padding:10px 22px; font-size:.9rem; font-weight:600; cursor:pointer; flex:1; min-width:120px; text-align:center; }
.btn-submit { background:#1a3a5c; color:#fff; border:none; border-radius:9px;
  padding:10px 22px; font-size:.9rem; font-weight:600; cursor:pointer;
  display:flex; align-items:center; gap:6px; }
.btn-submit:hover:not(:disabled) { background:#2d6a9f; }
.btn-submit:disabled, .btn-save:disabled { opacity:.6; cursor:not-allowed; }
.status-info { font-size:.82rem; color:#6b7280; display:flex; align-items:center; width:100%; justify-content:center; padding-top:4px; }

@media (max-width: 768px) {
  .section-nav { width:100%; display:flex; flex-direction:row; overflow-x:auto;
    height:auto; border-right:none; border-bottom:1px solid #e5e7eb; padding:0; }
  .section-btn { border-left:none; border-bottom:3px solid transparent; padding:10px 14px;
    white-space:nowrap; }
  .section-btn.active { border-left:none; border-bottom-color:#1a3a5c; }
  .editor-body { flex-direction:column; }
  .form-grid { grid-template-columns:1fr; }
  .col-span-2 { grid-column:span 1; }
  .section-content { padding:18px 16px; }
  .editor-topbar { flex-direction:column; align-items:flex-start; gap:8px; }
}
</style>